import _ from 'underscore';
import { Promise } from 'bluebird';
import { useCallback, useEffect, useState } from 'react';
import {
  fetchImgPromise, getImagePixels, createSelectedImageSrc, createHighlightedImage,
} from '@bingads-webui-campaign-react/ads-studio-app-shared-components';

export const useObjectSelection = ({
  originalImageUrl,
  originalImageWidth,
  originalImageHeight,
  segmentationMasks,
  imageUrlWithoutBackground,
  setSelectedImageSrc,
  selectedMasks,
  setSelectedMasks,
  i18n,
}) => {
  const [workingMasks, setWorkingMasks] = useState([]);
  const [workingSelectedImageSrc, setWorkingSelectedImageSrc] = useState(null);
  const [originalImagePixels, setOriginalImagePixels] = useState(null);
  const [initialSelectedImageSrc, setInitialSelectedImageSrc] = useState(null);
  const [borderImageSrc, setBorderImageSrc] = useState(null);
  const [initialBorderImageSrc, setInitialBorderImageSrc] = useState(null);
  const [highlightedImageSrc, setHighlightedImageSrc] = useState(null);

  // initial error message is null, after generating, it will be set to '' or error message
  const [objectSelectionError, setObjectSelectionError] = useState(null);

  useEffect(() => {
    // Get the original image pixels
    if (originalImageUrl) {
      fetchImgPromise(originalImageUrl).then((image) => {
        const pixels = getImagePixels(image, originalImageWidth, originalImageHeight);
        setOriginalImagePixels(pixels);
      });
    }
  }, [originalImageHeight, originalImageWidth, originalImageUrl]);


  const createSelectedImage = useCallback((selectedImageSrcProp, masks, isAdd) => {
    if (originalImagePixels && segmentationMasks) {
      return fetchImgPromise(selectedImageSrcProp || originalImageUrl)
        .then(image => createSelectedImageSrc(image, originalImageWidth, originalImageHeight, originalImagePixels, segmentationMasks, masks, isAdd, selectedImageSrcProp));
    }
    return Promise.resolve({
      borderImageSrc: null,
      selectedImageSrc: null,
    });
  }, [originalImagePixels, segmentationMasks, originalImageUrl, originalImageWidth, originalImageHeight]);

  useEffect(() => {
    if (imageUrlWithoutBackground && segmentationMasks) {
      setWorkingMasks(selectedMasks);
      createSelectedImage(null, selectedMasks, true).then(({
        borderImageSrc: newBorderImageSrc,
        selectedImageSrc: newImageSrc,
      }) => {
        setInitialBorderImageSrc(newBorderImageSrc);
        setInitialSelectedImageSrc(newImageSrc);
        setBorderImageSrc(newBorderImageSrc);
        setWorkingSelectedImageSrc(newImageSrc);
      });
    }
  }, [imageUrlWithoutBackground, segmentationMasks, createSelectedImage, selectedMasks]);

  useEffect(() => {
    if (imageUrlWithoutBackground && segmentationMasks) {
      if (_.isEmpty(workingMasks)) {
        setObjectSelectionError(i18n.getString(_TL_('Please select at least one subject or object you want to keep in the image.')));
      } else {
        setObjectSelectionError('');
      }
    }
  }, [i18n, imageUrlWithoutBackground, segmentationMasks, workingMasks]);

  const updateHighlightedImage = useCallback((maskValue) => {
    const highlightedImage = createHighlightedImage(originalImageWidth, originalImageHeight, originalImagePixels, segmentationMasks, workingMasks, maskValue);
    setHighlightedImageSrc(highlightedImage);
  }, [originalImageWidth, originalImageHeight, segmentationMasks, workingMasks, originalImagePixels]);

  const toggleSelectedMask = useCallback((maskId) => {
    let newMasks = [];
    const isAdd = !_.contains(workingMasks, maskId);
    if (!isAdd) {
      newMasks = _.without(workingMasks, maskId);
    } else {
      newMasks = [...workingMasks, maskId];
    }
    setWorkingMasks(_.sortBy(newMasks));
    createSelectedImage(workingSelectedImageSrc, [maskId], isAdd).then(({
      borderImageSrc: newBorderImageSrc,
      selectedImageSrc: newImageSrc,
    }) => {
      setBorderImageSrc(newBorderImageSrc);
      setWorkingSelectedImageSrc(newImageSrc);
    });
  }, [workingMasks, createSelectedImage, workingSelectedImageSrc]);

  const updateSubject = useCallback(() => {
    setSelectedMasks(workingMasks);
    setSelectedImageSrc(workingSelectedImageSrc);
  }, [setSelectedImageSrc, setSelectedMasks, workingMasks, workingSelectedImageSrc]);

  const startOver = useCallback(() => {
    setWorkingSelectedImageSrc(initialSelectedImageSrc);
    setBorderImageSrc(initialBorderImageSrc);
    setWorkingMasks(selectedMasks);
  }, [initialSelectedImageSrc, initialBorderImageSrc, selectedMasks]);

  return {
    borderImageSrc,
    toggleSelectedMask,
    workingSelectedImageSrc,
    highlightedImageSrc,
    startOver,
    updateSubject,
    updateHighlightedImage,
    objectSelectionError,
    workingMasks,
  };
};
